/**
 * Config Registry Table Component
 * DataGrid table for displaying config list with pagination
 */

import { useMemo, useState } from 'react';
import { Box, Tooltip, useTheme, Typography, Chip } from '@mui/material';
import {
  DataGridPro,
  GridColDef,
  GridActionsCellItem,
  GridRenderCellParams,
  GridPaginationModel,
  GridColumnVisibilityModel,
  GridDensity,
  GridSortModel,
} from '@mui/x-data-grid-pro';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import { useSelector } from 'react-redux';
import { useGlobalConfig } from 'context/GlobalContext';
import { ANALYTICS_EVENTS } from 'framework/analytics/AnalyticsEvents';
import {
  selectConfigRegistryConfigs,
  selectConfigRegistryPagination,
  selectConfigRegistryIsLoading,
} from '../store/configRegistryUISlice';
import { ConfigRegistryItem, CONFIG_RISK_LEVEL_COLORS } from '../types/configRegistryTypes';
import { ConfigStatusBadge } from './ConfigStatusBadge';
import { createColumnHeader } from './ColumnHeader';

interface ConfigRegistryTableProps {
  onPageChange: (page: number, pageSize: number) => void;
  onRowClick?: (config: ConfigRegistryItem) => void;
  onEdit?: (config: ConfigRegistryItem) => void;
}

// We'll use the built-in toolbar via slotProps instead of a custom component

export const ConfigRegistryTable = ({
  onPageChange,
  onRowClick,
  onEdit,
}: ConfigRegistryTableProps) => {
  const theme = useTheme();
  const { rc } = useGlobalConfig();

  const configs = useSelector(selectConfigRegistryConfigs);
  const pagination = useSelector(selectConfigRegistryPagination);
  const isLoading = useSelector(selectConfigRegistryIsLoading);

  const canEdit = rc.permissionsManager.canConfigRegistryEdit();

  // Column visibility state - hide additional columns by default
  const [columnVisibilityModel, setColumnVisibilityModel] = useState<GridColumnVisibilityModel>({
    configDataType: true,
    component: true,
    // Hide these columns by default - users can show them via column menu
    configDocumentation: false,
    configClassification: false,
    configRiskLevel: false,
    deprecated: false,
    isMandatoryField: false,
    version: false,
    createdAt: false,
    updatedAt: false,
  });

  // Density state
  const [density, setDensity] = useState<GridDensity>('standard');

  const columns = useMemo<GridColDef[]>(() => [
    {
      field: 'serialNo',
      headerName: '#',
      width: 70,
      sortable: false,
      hideable: false,
      resizable: false,
      align: 'center',
      headerAlign: 'center',
      renderHeader: createColumnHeader('#', 'serialNo'),
      renderCell: (params: GridRenderCellParams) => {
        const rowIndex = params.api.getRowIndexRelativeToVisibleRows(params.id);
        const serialNo = (pagination.page - 1) * pagination.pageSize + rowIndex + 1;
        return (
          <Typography variant="body2" sx={{ fontWeight: 500, color: 'text.secondary' }}>
            {serialNo}
          </Typography>
        );
      },
    },
    {
      field: 'configKey',
      headerName: 'Config Key',
      flex: 1,
      minWidth: 180,
      hideable: false, // Always show config key
      resizable: true,
      renderHeader: createColumnHeader('Config Key', 'configKey'),
      renderCell: (params: GridRenderCellParams) => (
        <Tooltip title={params.value || ''} placement="top" arrow>
          <Box sx={{
            fontFamily: 'monospace',
            fontSize: '0.85rem',
            color: theme.palette.primary.main,
            fontWeight: 500,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}>
            {params.value}
          </Box>
        </Tooltip>
      ),
    },
    {
      field: 'displayName',
      headerName: 'Display Name',
      flex: 1.2,
      minWidth: 180,
      resizable: true,
      renderHeader: createColumnHeader('Display Name', 'displayName'),
      renderCell: (params: GridRenderCellParams) => (
        <Tooltip title={params.value || ''} placement="top" arrow>
          <Box sx={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}>
            {params.value}
          </Box>
        </Tooltip>
      ),
    },
    {
      field: 'configType',
      headerName: 'Config Type',
      flex: 1,
      minWidth: 150,
      resizable: true,
      renderHeader: createColumnHeader('Config Type', 'configType'),
      renderCell: (params: GridRenderCellParams) => (
        <Tooltip title={params.value || ''} placement="top" arrow>
          <Chip
            label={params.value}
            size="small"
            variant="outlined"
            sx={{
              borderRadius: 1,
              fontWeight: 500,
              fontSize: '0.75rem',
              maxWidth: '100%',
            }}
          />
        </Tooltip>
      ),
    },
    {
      field: 'configDataType',
      headerName: 'Data Type',
      width: 120,
      resizable: true,
      renderHeader: createColumnHeader('Data Type', 'configDataType'),
      renderCell: (params: GridRenderCellParams) => (
        <Tooltip title={params.value || ''} placement="top" arrow>
          <Box sx={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}>
            {params.value}
          </Box>
        </Tooltip>
      ),
    },
    {
      field: 'verificationStatus',
      headerName: 'Status',
      width: 140,
      resizable: true,
      renderHeader: createColumnHeader('Status', 'verificationStatus'),
      renderCell: (params: GridRenderCellParams) => (
        <ConfigStatusBadge status={params.value} />
      ),
    },
    {
      field: 'component',
      headerName: 'Component',
      width: 150,
      resizable: true,
      renderHeader: createColumnHeader('Component', 'component'),
      renderCell: (params: GridRenderCellParams) => {
        const value = params.value;
        if (!value) return <Typography variant="body2" color="text.disabled">-</Typography>;
        // Handle different data types: array, string, or object with name/code
        let displayValue = '';
        if (Array.isArray(value)) {
          displayValue = value.map(v => typeof v === 'object' ? (v.name || v.code || v) : v).join(', ') || '-';
        } else if (typeof value === 'object') {
          displayValue = value.name || value.code || '-';
        } else {
          displayValue = String(value);
        }

        return (
          <Tooltip title={displayValue} placement="top" arrow>
            <Box sx={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}>
              {displayValue}
            </Box>
          </Tooltip>
        );
      },
    },
    {
      field: 'configDocumentation',
      headerName: 'Documentation',
      flex: 2,
      minWidth: 250,
      resizable: true,
      renderHeader: createColumnHeader('Documentation', 'configDocumentation'),
      renderCell: (params: GridRenderCellParams) => (
        <Tooltip title={params.value || 'No documentation'} placement="top" arrow>
          <Box sx={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            color: params.value ? 'text.secondary' : 'text.disabled',
            fontStyle: params.value ? 'normal' : 'italic',
          }}>
            {params.value || 'No documentation'}
          </Box>
        </Tooltip>
      ),
    },
    {
      field: 'configClassification',
      headerName: 'Classification',
      width: 140,
      resizable: true,
      renderHeader: createColumnHeader('Classification', 'configClassification'),
      renderCell: (params: GridRenderCellParams) => {
        if (!params.value) return <Typography variant="body2" color="text.disabled">-</Typography>;
        return (
          <Tooltip title={params.value} placement="top" arrow>
            <Chip
              label={params.value}
              size="small"
              variant="filled"
              color="info"
              sx={{
                borderRadius: 1,
                fontWeight: 500,
                fontSize: '0.75rem',
                maxWidth: '100%',
              }}
            />
          </Tooltip>
        );
      },
    },
    {
      field: 'configRiskLevel',
      headerName: 'Risk Level',
      width: 130,
      resizable: true,
      renderHeader: createColumnHeader('Risk Level', 'configRiskLevel'),
      renderCell: (params: GridRenderCellParams) => {
        if (!params.value) return <Typography variant="body2" color="text.disabled">-</Typography>;
        const colors = CONFIG_RISK_LEVEL_COLORS[params.value as keyof typeof CONFIG_RISK_LEVEL_COLORS];
        return (
          <Chip
            label={params.value}
            size="small"
            variant="filled"
            sx={{
              bgcolor: colors?.bg,
              color: colors?.color,
              borderRadius: 1,
              fontWeight: 600,
              fontSize: '0.7rem',
              maxWidth: '100%',
            }}
          />
        );
      },
    },
    {
      field: 'deprecated',
      headerName: 'Deprecated',
      width: 120,
      resizable: true,
      type: 'boolean',
      renderCell: (params: GridRenderCellParams) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {params.value ? (
            <>
              <CheckCircleIcon sx={{ fontSize: 18, color: 'warning.main' }} />
              <Typography variant="body2" color="warning.main">Yes</Typography>
            </>
          ) : (
            <>
              <CancelIcon sx={{ fontSize: 18, color: 'success.main' }} />
              <Typography variant="body2" color="text.secondary">No</Typography>
            </>
          )}
        </Box>
      ),
    },
    {
      field: 'isMandatoryField',
      headerName: 'Mandatory',
      width: 120,
      resizable: true,
      type: 'boolean',
      renderCell: (params: GridRenderCellParams) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {params.value ? (
            <>
              <CheckCircleIcon sx={{ fontSize: 18, color: 'error.main' }} />
              <Typography variant="body2" color="error.main">Yes</Typography>
            </>
          ) : (
            <>
              <CancelIcon sx={{ fontSize: 18, color: 'text.disabled' }} />
              <Typography variant="body2" color="text.secondary">No</Typography>
            </>
          )}
        </Box>
      ),
    },
    {
      field: 'version',
      headerName: 'Version',
      width: 100,
      resizable: true,
      type: 'number',
      renderHeader: createColumnHeader('Version', 'version'),
      renderCell: (params: GridRenderCellParams) => (
        <Chip
          label={`v${params.value || 1}`}
          size="small"
          variant="outlined"
          sx={{
            borderRadius: 1,
            fontWeight: 600,
            fontSize: '0.75rem',
            fontFamily: 'monospace',
          }}
        />
      ),
    },
    {
      field: 'createdAt',
      headerName: 'Created',
      width: 160,
      resizable: true,
      type: 'dateTime',
      renderCell: (params: GridRenderCellParams) => {
        if (!params.value) return <Typography variant="body2" color="text.disabled">-</Typography>;
        const date = new Date(params.value);
        const formatted = date.toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        });
        return (
          <Tooltip title={date.toLocaleString()} placement="top" arrow>
            <Typography variant="body2" sx={{ fontSize: '0.8125rem' }}>
              {formatted}
            </Typography>
          </Tooltip>
        );
      },
    },
    {
      field: 'updatedAt',
      headerName: 'Updated',
      width: 160,
      resizable: true,
      type: 'dateTime',
      renderCell: (params: GridRenderCellParams) => {
        if (!params.value) return <Typography variant="body2" color="text.disabled">-</Typography>;
        const date = new Date(params.value);
        const formatted = date.toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        });
        return (
          <Tooltip title={date.toLocaleString()} placement="top" arrow>
            <Typography variant="body2" sx={{ fontSize: '0.8125rem' }}>
              {formatted}
            </Typography>
          </Tooltip>
        );
      },
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Actions',
      width: 100,
      pinnable: true,
      hideable: false,
      renderHeader: createColumnHeader('Actions', 'actions'),
      cellClassName: 'actions-column',
      headerClassName: 'actions-column-header',
      getActions: ({ row }) => {
        const actions = [
          <GridActionsCellItem
            key="view"
            icon={<Tooltip title="View"><VisibilityIcon /></Tooltip>}
            label="View"
            onClick={() => onRowClick?.(row as ConfigRegistryItem)}
          />,
        ];

        if (canEdit) {
          actions.push(
            <GridActionsCellItem
              key="edit"
              icon={<Tooltip title="Edit"><EditIcon /></Tooltip>}
              label="Edit"
              onClick={() => onEdit?.(row as ConfigRegistryItem)}
            />
          );
        }

        return actions;
      },
    },
  ], [theme, canEdit, onRowClick, onEdit, pagination.page, pagination.pageSize]);

  const handlePaginationModelChange = (model: GridPaginationModel) => {
    onPageChange(model.page + 1, model.pageSize);
  };

  // Fire SORT_CHANGED on a discrete sort model change (column header click).
  const handleSortModelChange = (model: GridSortModel) => {
    if (model.length > 0) {
      rc.analytics.trackEvent(ANALYTICS_EVENTS.CONFIG_REGISTRY.SORT_CHANGED, {
        field_name: model[0].field,
        sort_direction: model[0].sort || undefined,
      });
    }
  };

  return (
    <Box sx={{
      width: '100%',
      height: { xs: 'calc(100vh - 320px)', sm: 'calc(100vh - 280px)' },
      minHeight: 400,
      border: `1px solid ${theme.palette.divider}`,
      borderRadius: 2,
      overflow: 'hidden',
      bgcolor: 'background.paper',
      boxShadow: theme.palette.mode === 'dark'
        ? '0 4px 6px -1px rgba(0, 0, 0, 0.3), 0 2px 4px -1px rgba(0, 0, 0, 0.2)'
        : '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
    }}>
      <DataGridPro
        rows={configs || []}
        columns={columns}
        loading={isLoading}
        getRowId={(row) => row.documentId}

        // Pagination
        pagination
        paginationMode="server"
        rowCount={pagination.total}
        paginationModel={{
          page: pagination.page - 1,
          pageSize: pagination.pageSize,
        }}
        onPaginationModelChange={handlePaginationModelChange}
        onSortModelChange={handleSortModelChange}
        pageSizeOptions={[10, 25, 50, 100]}

        // Row interaction
        disableRowSelectionOnClick
        onRowClick={(params) => onRowClick?.(params.row as ConfigRegistryItem)}
        getRowClassName={(params) =>
          params.indexRelativeToCurrentPage % 2 === 0 ? 'even-row' : 'odd-row'
        }

        // Column features
        columnVisibilityModel={columnVisibilityModel}
        onColumnVisibilityModelChange={setColumnVisibilityModel}

        // Density
        density={density}
        onDensityChange={setDensity}

        // Initial state with pinned columns
        initialState={{
          pinnedColumns: { right: ['actions'] },
          columns: {
            columnVisibilityModel: {
              // Show these columns by default
              configDataType: true,
              component: true,
              // Hide additional columns by default - users can show via column menu
              configDocumentation: false,
                        configClassification: false,
              configRiskLevel: false,
              deprecated: false,
              isMandatoryField: false,
              version: false,
              createdAt: false,
              updatedAt: false,
            },
          },
          filter: {
            filterModel: {
              items: [],
              quickFilterValues: [],
            },
          },
        }}

        // Enable filtering
        filterMode="client"

        // Styling
        sx={{
          border: 'none',
          bgcolor: 'background.paper',
          '& .MuiDataGrid-columnHeaders': {
            bgcolor: theme.palette.mode === 'dark' ? 'grey.800' : '#f8f9fa',
            borderBottom: `2px solid ${theme.palette.divider}`,
            '& .MuiDataGrid-columnHeaderTitle': {
              fontWeight: 600,
              fontSize: '0.8125rem',
              color: theme.palette.text.secondary,
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
            },
          },
          '& .MuiDataGrid-row': {
            cursor: 'pointer',
            transition: 'background-color 0.15s ease',
            '&.even-row': {
              bgcolor: 'background.paper',
            },
            '&.odd-row': {
              bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.015)',
            },
            '&:hover': {
              bgcolor: theme.palette.mode === 'dark'
                ? 'rgba(25, 118, 210, 0.12)'
                : 'rgba(25, 118, 210, 0.06)',
            },
          },
          '& .MuiDataGrid-cell': {
            borderBottom: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.06)'}`,
            py: 1,
            fontSize: '0.875rem',
            display: 'flex',
            alignItems: 'center',
            '&:focus': {
              outline: 'none',
            },
            '&:focus-within': {
              outline: 'none',
            },
          },
          '& .MuiDataGrid-footerContainer': {
            borderTop: `2px solid ${theme.palette.divider}`,
            bgcolor: theme.palette.mode === 'dark' ? 'grey.800' : '#f8f9fa',
          },
          '& .MuiDataGrid-virtualScroller': {
            bgcolor: 'background.paper',
          },
          '& .MuiDataGrid-columnSeparator': {
            display: 'none',
          },
          // Actions column - vertical separator line
          '& .actions-column-header': {
            borderLeft: `2px solid ${theme.palette.divider}`,
          },
          '& .actions-column': {
            borderLeft: `2px solid ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'}`,
          },
          // Pinned column styling
          '& .MuiDataGrid-pinnedColumns': {
            boxShadow: theme.palette.mode === 'dark'
              ? '-4px 0 8px -2px rgba(0, 0, 0, 0.5)'
              : '-4px 0 8px -2px rgba(0, 0, 0, 0.1)',
          },
          // Loading overlay
          '& .MuiDataGrid-overlay': {
            bgcolor: theme.palette.mode === 'dark'
              ? 'rgba(0, 0, 0, 0.5)'
              : 'rgba(255, 255, 255, 0.8)',
            backdropFilter: 'blur(2px)',
          },
          // Empty state
          '& .MuiDataGrid-overlayWrapper': {
            minHeight: 400,
          },
        }}
      />
    </Box>
  );
};

export default ConfigRegistryTable;

